import React from 'react';
import { ChevronDown } from 'lucide-react';
import { GroupTag } from '../../../types/protocols';
import { BuildingType, iconForBuildingType } from '../../../types/BuildingTypeEnums';
import { FittingGroupTags } from '../../../Components/FittingGroupTags';
import { FirebaseImage, ImageSize } from '../../../Helpers/ImageLoader';
import { placeholderForBuildingType } from '../../../Helpers/PlaceholderUtils';
import { PROJECT_ICON } from './ProjectBoardCard';
// The group card's rules live with the project board's; Förvaltning uses the card too
import '../ProjectBoardPage.css';

interface BuildingGroupCardProps {
    /** Unique per column, for the toggle's aria-controls */
    groupId: string;
    buildingName?: string;
    buildingType?: BuildingType;
    coverPhotoPath?: string;
    groupTags?: { [key: string]: GroupTag };
    /** Items of this building in the column */
    itemCount: number;
    /** What the items are, shown on the toggle as "<label> (n)"; default "Projekt" */
    itemLabel?: string;
    /** Icon before the label; default the project icon */
    itemIcon?: React.ReactNode;
    expanded: boolean;
    onToggle: () => void;
    /** The building's cards, shown indented under the card while expanded */
    children: React.ReactNode;
}

/**
 * One building's cards in a board column (Projekt's all-buildings board,
 * Förvaltning sorted by building): a card with the building's cover thumbnail
 * beside its name and group tags (one line each) and a "<label> (n)" toggle,
 * with the cards indented under it while expanded.
 */
const BuildingGroupCard: React.FC<BuildingGroupCardProps> = ({
    groupId,
    buildingName,
    buildingType,
    coverPhotoPath,
    groupTags,
    itemCount,
    itemLabel = 'Projekt',
    itemIcon = <img src={PROJECT_ICON} alt="" className="project-board-card__toggle-icon" />,
    expanded,
    onToggle,
    children
}) => {
    const listId = `project-board-group-${groupId}`;
    const hasTags = Object.keys(groupTags ?? {}).length > 0;

    return (
        <div className="project-board-group">
            <div className="management-item project-board-group__card">
                <div className="project-board-group__header">
                    <FirebaseImage
                        storagePath={coverPhotoPath}
                        size={ImageSize.THUMBNAIL_SMALL}
                        placeholder={placeholderForBuildingType(buildingType)}
                        className="project-board-group__thumb"
                        loading="lazy"
                    />
                    <div className="project-board-group__info">
                        {/* One line each: the name cut short with "…", the tags
                            that fit and "+n" for the rest */}
                        <div className="project-board-group__name" title={buildingName}>
                            <img src={iconForBuildingType(buildingType)} alt="" className="project-board-group__icon" />
                            <span className="project-board-group__name-text">{buildingName ?? 'Okänd byggnad'}</span>
                        </div>
                        {hasTags && groupTags && <FittingGroupTags groupTags={groupTags} />}
                    </div>
                </div>
                <button
                    type="button"
                    className="project-board-card__toggle"
                    onClick={onToggle}
                    aria-expanded={expanded}
                    aria-controls={listId}
                >
                    {itemIcon}
                    <span>{itemLabel} ({itemCount})</span>
                    <ChevronDown size={16} className="project-board-card__chevron" aria-hidden="true" />
                </button>
            </div>
            {expanded && (
                <div id={listId} className="project-board-group__projects">
                    {children}
                </div>
            )}
        </div>
    );
};

export default BuildingGroupCard;
